import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let mounted = true;

    const applyResult = (hasSession: boolean) => {
      if (mounted) {
        setSessionReady(hasSession);
        setCheckingSession(false);
      }
    };

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          applyResult(true);
          return;
        }
      } catch {
        // kontynuuj do opóźnionej próby
      }
      // Hash z linku resetującego może być przetworzony asynchronicznie – sprawdź ponownie
      setTimeout(async () => {
        if (!mounted) return;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          applyResult(!!session);
        } catch {
          applyResult(false);
        }
      }, 600);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted && session) applyResult(true);
    });

    checkSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Hasło musi mieć minimum 8 znaków');
      return;
    }

    if (password !== confirmPassword) {
      setError('Hasła muszą być identyczne');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) throw error;

      // Użytkownik ma już sesję po zmianie hasła – przekieruj do aplikacji
      window.location.href = '/';
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Wystąpił błąd';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div
        className="min-h-viewport"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <div style={{ fontSize: '16px', color: '#666' }}>Ładowanie...</div>
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '10px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '400px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#c62828', marginBottom: '20px' }}>
            Ten link wygasł lub jest nieprawidłowy. Poproś o nowy link resetujący.
          </p>
          <a
            href="/forgot-password"
            style={{
              color: '#4CAF50',
              textDecoration: 'underline',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Wyślij ponownie link
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-viewport"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px',
        }}
      >
        <h1 style={{ textAlign: 'center', marginBottom: '10px', color: '#333' }}>
          Ustaw nowe hasło
        </h1>
        <p
          style={{
            textAlign: 'center',
            color: '#666',
            fontSize: '14px',
            marginBottom: '20px',
          }}
        >
          Wprowadź nowe hasło (minimum 8 znaków).
        </p>

        {error && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '5px',
              marginBottom: '20px',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                color: '#555',
              }}
            >
              Nowe hasło
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '16px',
                boxSizing: 'border-box',
              }}
              placeholder="Minimum 8 znaków"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                color: '#555',
              }}
            >
              Powtórz hasło
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '16px',
                boxSizing: 'border-box',
              }}
              placeholder="Powtórz hasło"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '15px',
            }}
          >
            {loading ? 'Ładowanie...' : 'Zmień hasło'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <a
            href="/login"
            style={{
              color: '#4CAF50',
              textDecoration: 'underline',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Wróć do logowania
          </a>
        </div>
      </div>
    </div>
  );
};
